import { Link } from 'react-router-dom';
import styles from './SignIn.module.css';

/** The support address named in the mission brief, mirrored from the
 *  entity/contact block already locked in docs/legal/privacy-policy.md §1 /
 *  terms-of-service.md §1 (Underland Ventures LLC's public contact) — the
 *  SAME address, not a second invented one. */
const SUPPORT_EMAIL = 'underlandventures@gmail.com';

interface SignInProps {
  onSignIn: () => void;
}

interface HowItWorksBeat {
  step: number;
  title: string;
  body: string;
}

/** The 3-beat "how it works" strip, rendered ON the sign-in screen (Jason,
 *  direct spec: "a welcome to Mira... and this is how it works step by
 *  step... a quick start guide" — this is the STRANGER-facing preview of
 *  that same shape, before they ever sign in, not the dismissible in-app
 *  guide itself — see lib/quickStartGuide.ts for that). Deliberately a
 *  3-beat summary, not the full 6-step walkthrough: a stranger deciding
 *  whether to sign in needs the shape of the whole journey in one glance,
 *  not every deep-link destination yet (those only make sense once they're
 *  actually in the app — QuickStartGuide.tsx covers that). Plain copy, en-US,
 *  matches the brief's own words ("tell your story -> she finds the fits ->
 *  you decide, apply, track"). */
const HOW_IT_WORKS: HowItWorksBeat[] = [
  { step: 1, title: 'Tell your story', body: 'Upload a resume or paste your profile — I listen.' },
  { step: 2, title: 'I find the fits', body: "I watch the boards and bring back roles worth your time." },
  { step: 3, title: 'You decide, apply, track', body: 'Tailor, send, and track — always your call, never automatic.' },
];

/**
 * Diagram node 0 — landing -> sign-in (c3-onboarding-2026-07-04.svg). The
 * stranger's very first screen: one button, Google OAuth, no mic/camera ask
 * here (that only ever happens later, in-context, at the actual mic tap —
 * see VoiceTextarea/useDictation). Calls useAuth's EXISTING signIn(), which
 * already does the real thing (window.location.href = /auth/google?return_to=…,
 * lib/useAuth.ts:41-44) — this component adds no new auth wiring, only the
 * first screen that calls it.
 *
 * Rendered by App.tsx INSTEAD of AppShell/the tab bar whenever
 * useAuth().user is null — a signed-out stranger never reaches a single
 * requireAuth-gated route (GET /corpus/roles, /auth/me, etc. all 401 without
 * a session), so gating at this level is not just presentational, it matches
 * what the backend already enforces.
 *
 * LANE U1 (Jason, direct: "Login page needs enhanced, needs to be more
 * redone"): elevated from a bare card to a real front door — Mira's presence
 * (the M avatar, enlarged/composed against a soft halo), the one-line
 * promise, and the 3-beat "how it works" strip above, so a stranger
 * understands the whole shape of the product BEFORE they ever click sign-in
 * — then the same Google button + no-mic reassurance this screen always
 * had. Shares SignIn.module.css with Goodbye.tsx (R207) — the new classes
 * added here (.halo/.avatarLarge/.promise/.howItWorks/.beat*) are additive;
 * every class Goodbye.tsx already used (.page/.card/.avatar/.title/.body/
 * .googleButton) is untouched, so that screen's appearance does not change.
 *
 * ENROLL lane (custom login + first-run enrollment consent — Jason's own
 * framing: "a real branded login"): two ADDITIVE elements only, the 3-beat
 * pitch above is byte-unchanged. (1) A wordmark slot directly above the
 * existing halo/avatar — deliberately NAME-AGNOSTIC (the naming decision is
 * still in flux; R265 in docs/CHANGE-CONTROL.md records a chosen name but no
 * component in this tree has been repointed to it yet, and the mission's own
 * instruction is "name-agnostic" — this renders the sage/teal brand mark
 * ITSELF, not a text string that could go stale the moment the name changes
 * again). (2) A legal footer below the existing hint line: Terms · Privacy ·
 * the support email, linking to the SAME /terms and /privacy routes
 * Settings/ConsentGate use (lib/legalRoute.ts, App.tsx's own top-of-render
 * pathname gate — reachable signed-out, exactly what a stranger reading this
 * screen needs).
 */
export function SignIn({ onSignIn }: SignInProps) {
  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.wordmark} aria-hidden="true">
          <span className={styles.wordmarkDot} />
        </div>
        <div className={styles.halo}>
          <div className={styles.avatarLarge}>M</div>
        </div>
        <div className={styles.title}>Hi, I'm Meira.</div>
        <div className={styles.promise}>
          I find the real story in your work, and put it in front of the people who need it.
        </div>
        <div className={styles.body}>
          Job hunting is hard, and writing about yourself is harder. I'm here to listen, find the
          threads in your work, and help you put your real self first.
        </div>

        <div className={styles.howItWorks}>
          {HOW_IT_WORKS.map((beat) => (
            <div key={beat.step} className={styles.beat}>
              <div className={styles.beatNumber}>{beat.step}</div>
              <div className={styles.beatBody}>
                <div className={styles.beatTitle}>{beat.title}</div>
                <div className={styles.beatText}>{beat.body}</div>
              </div>
            </div>
          ))}
        </div>

        <button type="button" className={styles.googleButton} onClick={onSignIn}>
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="#4285F4"
              d="M23.52 12.27c0-.85-.08-1.67-.22-2.45H12v4.63h6.48a5.54 5.54 0 0 1-2.4 3.63v3h3.88c2.27-2.09 3.56-5.17 3.56-8.81Z"
            />
            <path
              fill="#34A853"
              d="M12 24c3.24 0 5.96-1.07 7.95-2.92l-3.88-3c-1.08.72-2.46 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.26v3.11A12 12 0 0 0 12 24Z"
            />
            <path
              fill="#FBBC05"
              d="M5.27 14.27A7.2 7.2 0 0 1 4.89 12c0-.79.14-1.56.38-2.27V6.62H1.26A12 12 0 0 0 0 12c0 1.94.46 3.77 1.26 5.38l4.01-3.11Z"
            />
            <path
              fill="#EA4335"
              d="M12 4.77c1.77 0 3.35.61 4.6 1.8l3.45-3.45C17.95 1.19 15.24 0 12 0A12 12 0 0 0 1.26 6.62l4.01 3.11C6.22 6.88 8.87 4.77 12 4.77Z"
            />
          </svg>
          Continue with Google
        </button>
        <div className={styles.hint}>Sign in with Google — then Meira does her best listening, out loud.</div>

        <div className={styles.legalFooter}>
          <Link to="/terms" className={styles.legalLink}>
            Terms
          </Link>
          <span className={styles.legalDivider} aria-hidden="true">
            ·
          </span>
          <Link to="/privacy" className={styles.legalLink}>
            Privacy
          </Link>
          <span className={styles.legalDivider} aria-hidden="true">
            ·
          </span>
          <a href={`mailto:${SUPPORT_EMAIL}`} className={styles.legalLink}>
            {SUPPORT_EMAIL}
          </a>
        </div>
      </div>
    </div>
  );
}
